import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/config';
import DashboardHeader from '../common/DashboardHeader';
import {
  UserGroupIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  UsersIcon,
  DocumentTextIcon,
  CogIcon,
  BellIcon,
  KeyIcon,
  ServerIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ClockIcon,
  StarIcon,
  AdjustmentsHorizontalIcon,
  UserPlusIcon,
  BuildingStorefrontIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  PlusIcon,
  ArrowPathIcon,
  PhotoIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  totalGarages: number;
  activeGarages: number;
  systemHealth: {
    status: 'healthy' | 'warning' | 'critical';
    uptime: number;
    cpuUsage: number;
    memoryUsage: number;
    diskUsage: number;
  };
  recentActivities: Activity[];
  userGrowth: number[];
  garageGrowth: number[];
}

interface Activity {
  id: string;
  type: 'user' | 'garage' | 'system' | 'security';
  action: string;
  user?: string;
  timestamp: string;
  status: 'success' | 'warning' | 'error';
  details?: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  status: 'active' | 'inactive' | 'suspended';
  lastLogin: string;
  createdAt: string;
  garage?: {
    id: string;
    name: string;
  };
}

interface Garage {
  id: string;
  name: string;
  owner: string;
  email: string;
  phone: string;
  address: string;
  status: 'active' | 'inactive' | 'suspended';
  subscription: {
    plan: string;
    status: string;
    endDate: string;
  };
  createdAt: string;
}

interface QuickStat {
  label: string;
  value: number | string;
  change?: number;
  icon: React.ReactNode;
  color: string;
}

const EnhancedAdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [recentUsers, setRecentUsers] = useState<User[]>([]);
  const [recentGarages, setRecentGarages] = useState<Garage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [garageFilter, setGarageFilter] = useState('all');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // In production, these would be actual API calls
      // For now, setting mock data
      setMockData();
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const setMockData = () => {
    setStats({
      totalUsers: 856,
      activeUsers: 742,
      totalGarages: 128,
      activeGarages: 115,
      systemHealth: {
        status: 'healthy',
        uptime: 99.9,
        cpuUsage: 35,
        memoryUsage: 62,
        diskUsage: 48
      },
      recentActivities: [
        {
          id: '1',
          type: 'user',
          action: 'New user registration',
          user: 'Ahmed Al-Rashid',
          timestamp: '2 minutes ago',
          status: 'success'
        },
        {
          id: '2',
          type: 'garage',
          action: 'Garage suspended for violation',
          user: 'كراج النجمة',
          timestamp: '15 minutes ago',
          status: 'warning',
          details: 'Multiple customer complaints'
        },
        {
          id: '3',
          type: 'system',
          action: 'Backup completed successfully',
          timestamp: '1 hour ago',
          status: 'success'
        },
        {
          id: '4',
          type: 'security',
          action: 'Failed login attempt detected',
          user: 'unknown@example.com',
          timestamp: '2 hours ago',
          status: 'error',
          details: '5 failed attempts from IP 192.168.1.100'
        },
        {
          id: '5',
          type: 'user',
          action: 'User account reactivated',
          user: 'Sara Mohammed',
          timestamp: '3 hours ago',
          status: 'success'
        }
      ],
      userGrowth: [620, 645, 678, 702, 735, 768, 798, 825, 842, 856],
      garageGrowth: [95, 98, 102, 108, 112, 115, 118, 122, 125, 128]
    });

    setRecentUsers([
      {
        id: '1',
        name: 'Ahmed Al-Rashid',
        email: 'ahmed@example.com',
        role: 'garage_owner',
        status: 'active',
        lastLogin: '2024-01-15 14:30',
        createdAt: '2023-12-01',
        garage: {
          id: 'g1',
          name: 'كراج الأمل'
        }
      },
      {
        id: '2',
        name: 'Fatima Hassan',
        email: 'fatima@example.com',
        role: 'customer',
        status: 'active',
        lastLogin: '2024-01-15 10:15',
        createdAt: '2023-11-15'
      },
      {
        id: '3',
        name: 'Mohammed Ali',
        email: 'mohammed@example.com',
        role: 'employee',
        status: 'inactive',
        lastLogin: '2024-01-10 09:00',
        createdAt: '2023-10-20',
        garage: {
          id: 'g2',
          name: 'مركز الصيانة المتقدم'
        }
      },
      {
        id: '4',
        name: 'Sara Ibrahim',
        email: 'sara@example.com',
        role: 'customer',
        status: 'suspended',
        lastLogin: '2024-01-05 16:45',
        createdAt: '2023-09-10'
      },
      {
        id: '5',
        name: 'Khalid Omar',
        email: 'khalid@example.com',
        role: 'garage_owner',
        status: 'active',
        lastLogin: '2024-01-15 11:20',
        createdAt: '2023-08-05',
        garage: {
          id: 'g3',
          name: 'ورشة النخبة'
        }
      }
    ]);

    setRecentGarages([
      {
        id: 'g1',
        name: 'كراج الأمل',
        owner: 'Ahmed Al-Rashid',
        email: 'amal@garage.com',
        phone: '+965 1234 5678',
        address: 'Salmiya, Block 10',
        status: 'active',
        subscription: {
          plan: 'Professional',
          status: 'active',
          endDate: '2024-12-31'
        },
        createdAt: '2023-12-01'
      },
      {
        id: 'g2',
        name: 'مركز الصيانة المتقدم',
        owner: 'Hassan Mohammed',
        email: 'advanced@garage.com',
        phone: '+965 2345 6789',
        address: 'Hawally, Block 5',
        status: 'active',
        subscription: {
          plan: 'Enterprise',
          status: 'active',
          endDate: '2024-11-15'
        },
        createdAt: '2023-10-20'
      },
      {
        id: 'g3',
        name: 'ورشة النخبة',
        owner: 'Khalid Omar',
        email: 'elite@garage.com',
        phone: '+965 3456 7890',
        address: 'Farwaniya, Block 3',
        status: 'suspended',
        subscription: {
          plan: 'Professional',
          status: 'suspended',
          endDate: '2024-09-30'
        },
        createdAt: '2023-08-05'
      }
    ]);
  };

  const quickStats: QuickStat[] = [
    {
      label: 'Total Users',
      value: stats?.totalUsers || 0,
      change: 12.5,
      icon: <UsersIcon className="w-8 h-8" />,
      color: 'primary'
    },
    {
      label: 'Active Users',
      value: stats?.activeUsers || 0,
      change: 8.3,
      icon: <UserGroupIcon className="w-8 h-8" />,
      color: 'success'
    },
    {
      label: 'Total Garages',
      value: stats?.totalGarages || 0,
      change: 5.2,
      icon: <BuildingStorefrontIcon className="w-8 h-8" />,
      color: 'secondary'
    },
    {
      label: 'System Health',
      value: `${stats?.systemHealth.uptime || 0}%`,
      icon: <ServerIcon className="w-8 h-8" />,
      color: stats?.systemHealth.status === 'healthy' ? 'success' : 'warning'
    }
  ];

  const renderQuickStat = (stat: QuickStat) => {
    const colorMap: { [key: string]: string } = {
      primary: 'border-[#008B8B] text-[#008B8B]',
      secondary: 'border-[#FF8C00] text-[#FF8C00]',
      success: 'border-[#10B981] text-[#10B981]',
      warning: 'border-[#F59E0B] text-[#F59E0B]',
      danger: 'border-[#EF4444] text-[#EF4444]'
    };

    return (
      <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#008B8B]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{stat.label}</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">{stat.value}</p>
            {stat.change && (
              <div className="mt-2 flex items-center">
                {stat.change > 0 ? (
                  <ArrowUpIcon className="w-4 h-4 text-green-500 mr-1" />
                ) : (
                  <ArrowDownIcon className="w-4 h-4 text-red-500 mr-1" />
                )}
                <span className={`text-sm font-medium ${stat.change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {Math.abs(stat.change)}%
                </span>
                <span className="text-sm text-gray-500 ml-1">vs last month</span>
              </div>
            )}
          </div>
          <div className={`${colorMap[stat.color]}`}>{stat.icon}</div>
        </div>
      </div>
    );
  };

  const renderActivityIcon = (type: string) => {
    switch (type) {
      case 'user':
        return <UserGroupIcon className="w-5 h-5" />;
      case 'garage':
        return <BuildingStorefrontIcon className="w-5 h-5" />;
      case 'system':
        return <ServerIcon className="w-5 h-5" />;
      case 'security':
        return <ShieldCheckIcon className="w-5 h-5" />;
      default:
        return <BellIcon className="w-5 h-5" />;
    }
  };

  const renderActivityStatus = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderUserGrowthChart = () => {
    const data = {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct'],
      datasets: [
        {
          label: 'Total Users',
          data: stats?.userGrowth || [],
          borderColor: 'rgb(0, 139, 139)',
          backgroundColor: 'rgba(0, 139, 139, 0.1)',
          fill: true,
          tension: 0.4
        },
        {
          label: 'Garages',
          data: stats?.garageGrowth || [],
          borderColor: 'rgb(255, 140, 0)',
          backgroundColor: 'rgba(255, 140, 0, 0.1)',
          fill: true,
          tension: 0.4
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
        },
        title: {
          display: true,
          text: 'User & Garage Growth'
        }
      },
      scales: {
        y: {
          beginAtZero: true
        }
      }
    };

    return <Line data={data} options={options} />;
  };

  const renderSystemHealthChart = () => {
    if (!stats?.systemHealth) return null;

    const data = {
      labels: ['CPU', 'Memory', 'Disk'],
      datasets: [{
        data: [
          stats.systemHealth.cpuUsage,
          stats.systemHealth.memoryUsage,
          stats.systemHealth.diskUsage
        ],
        backgroundColor: [
          'rgba(0, 139, 139, 0.8)',
          'rgba(255, 140, 0, 0.8)',
          'rgba(16, 185, 129, 0.8)'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right' as const,
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        title: {
          display: true,
          text: 'System Resource Usage'
        }
      }
    };

    return <Doughnut data={data} options={options} />;
  };

  const renderUserManagementSection = () => {
    const filteredUsers = recentUsers.filter(user => {
      const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          user.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = userFilter === 'all' || user.status === userFilter;
      return matchesSearch && matchesFilter;
    });

    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">User Management</h3>
          <button 
            onClick={() => navigate('/dashboard/users')}
            className="bg-[#008B8B] text-white px-4 py-2 rounded-lg hover:bg-[#006666] flex items-center"
          >
            <UserPlusIcon className="w-5 h-5 mr-2" />
            Add User
          </button>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
            />
          </div>
          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B]"
          >
            <option value="all">All Users</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>

        {/* Users Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">User</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Role</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Last Login</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="py-4 px-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{user.name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-gray-700 capitalize">
                      {user.role.replace('_', ' ')}
                    </span>
                    {user.garage && (
                      <p className="text-xs text-gray-500">{user.garage.name}</p>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      user.status === 'active' ? 'bg-green-100 text-green-800' :
                      user.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-sm text-gray-700">
                    {user.lastLogin}
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex space-x-2">
                      <button className="text-[#008B8B] hover:text-[#006666]">
                        <EyeIcon className="w-5 h-5" />
                      </button>
                      <button className="text-blue-600 hover:text-blue-800">
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button className="text-red-600 hover:text-red-800">
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-700">
            Showing {filteredUsers.length} of {recentUsers.length} users
          </p>
          <button 
            onClick={() => navigate('/dashboard/users')}
            className="text-sm text-[#008B8B] hover:text-[#006666] font-medium"
          >
            View All Users →
          </button>
        </div>
      </div>
    );
  };

  const renderGarageManagementSection = () => {
    const filteredGarages = recentGarages.filter(garage => {
      const matchesSearch = garage.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          garage.owner.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = garageFilter === 'all' || garage.status === garageFilter;
      return matchesSearch && matchesFilter;
    });

    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Garage Management</h3>
          <button 
            onClick={() => navigate('/dashboard/garages')}
            className="bg-[#FF8C00] text-white px-4 py-2 rounded-lg hover:bg-[#CC7000] flex items-center"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Garage
          </button>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search garages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent"
            />
          </div>
          <select
            value={garageFilter}
            onChange={(e) => setGarageFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF8C00]"
          >
            <option value="all">All Garages</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>

        {/* Garages Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredGarages.map((garage) => (
            <div key={garage.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">{garage.name}</h4>
                  <p className="text-sm text-gray-600">Owner: {garage.owner}</p>
                </div>
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                  garage.status === 'active' ? 'bg-green-100 text-green-800' :
                  garage.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {garage.status}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm">
                  <EnvelopeIcon className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-gray-700">{garage.email}</span>
                </div>
                <div className="flex items-center text-sm">
                  <PhoneIcon className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-gray-700">{garage.phone}</span>
                </div>
                <div className="flex items-center text-sm">
                  <MapPinIcon className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-gray-700">{garage.address}</span>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Subscription</span>
                  <span className={`text-sm font-medium ${
                    garage.subscription.status === 'active' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {garage.subscription.plan}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Expires: {new Date(garage.subscription.endDate).toLocaleDateString()}
                </p>
              </div>

              <div className="flex space-x-2 mt-4">
                <button className="flex-1 px-3 py-2 bg-[#008B8B] text-white text-sm rounded hover:bg-[#006666]">
                  View Details
                </button>
                <button className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50">
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-gray-700">
            Showing {filteredGarages.length} of {recentGarages.length} garages
          </p>
          <button 
            onClick={() => navigate('/dashboard/garages')}
            className="text-sm text-[#FF8C00] hover:text-[#CC7000] font-medium"
          >
            View All Garages →
          </button>
        </div>
      </div>
    );
  };

  const renderSystemHealthSection = () => {
    if (!stats?.systemHealth) return null;

    const healthMetrics = [
      { label: 'CPU Usage', value: stats.systemHealth.cpuUsage, threshold: 80 },
      { label: 'Memory Usage', value: stats.systemHealth.memoryUsage, threshold: 85 },
      { label: 'Disk Usage', value: stats.systemHealth.diskUsage, threshold: 90 }
    ];

    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">System Health</h3>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            stats.systemHealth.status === 'healthy' ? 'bg-green-100 text-green-800' :
            stats.systemHealth.status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
            'bg-red-100 text-red-800'
          }`}>
            <span className={`w-2 h-2 rounded-full mr-2 ${
              stats.systemHealth.status === 'healthy' ? 'bg-green-600' :
              stats.systemHealth.status === 'warning' ? 'bg-yellow-600' :
              'bg-red-600'
            }`}></span>
            {stats.systemHealth.status.charAt(0).toUpperCase() + stats.systemHealth.status.slice(1)}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-sm text-gray-600 mb-2">Uptime</p>
            <p className="text-3xl font-bold text-gray-900">{stats.systemHealth.uptime}%</p>
            <p className="text-xs text-gray-500 mt-1">Last 30 days</p>
          </div>
          <div className="h-48">
            {renderSystemHealthChart()}
          </div>
        </div>

        <div className="space-y-4">
          {healthMetrics.map((metric, index) => (
            <div key={index}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-700">{metric.label}</span>
                <span className={`text-sm font-medium ${
                  metric.value > metric.threshold ? 'text-red-600' : 'text-gray-700'
                }`}>
                  {metric.value}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${
                    metric.value > metric.threshold ? 'bg-red-500' :
                    metric.value > metric.threshold * 0.8 ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`}
                  style={{width: `${metric.value}%`}}
                ></div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex space-x-3">
          <button className="flex-1 px-4 py-2 bg-[#008B8B] text-white rounded-lg hover:bg-[#006666]">
            View Logs
          </button>
          <button className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
            System Settings
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader 
        userName={user?.name}
        userRole="admin"
        showNotifications={true}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Admin Dashboard
              </h1>
              <p className="text-gray-600 mt-1">
                Manage users, garages, and system settings
              </p>
            </div>
            <div className="mt-4 lg:mt-0 flex flex-wrap gap-3">
              {/* View Selector */}
              <div className="flex bg-white rounded-lg shadow">
                {['overview', 'users', 'garages', 'system'].map((view) => (
                  <button
                    key={view}
                    onClick={() => setSelectedView(view)}
                    className={`px-4 py-2 text-sm font-medium capitalize ${
                      selectedView === view
                        ? 'bg-[#008B8B] text-white'
                        : 'text-gray-700 hover:bg-gray-50'
                    } ${
                      view === 'overview' ? 'rounded-l-lg' : 
                      view === 'system' ? 'rounded-r-lg' : ''
                    }`}
                  >
                    {view}
                  </button>
                ))}
              </div>

              <button 
                onClick={() => window.location.reload()}
                className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <ArrowPathIcon className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Content based on selected view */}
        {selectedView === 'overview' && (
          <>
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {quickStats.map((stat, index) => (
                <div key={index}>{renderQuickStat(stat)}</div>
              ))}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="h-64">
                  {renderUserGrowthChart()}
                </div>
              </div>

              {/* Recent Activities */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h3>
                <div className="space-y-4 max-h-56 overflow-y-auto">
                  {stats?.recentActivities.map((activity) => (
                    <div key={activity.id} className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg ${renderActivityStatus(activity.status)}`}>
                        {renderActivityIcon(activity.type)}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                        {activity.user && (
                          <p className="text-sm text-gray-600">{activity.user}</p>
                        )}
                        {activity.details && (
                          <p className="text-xs text-gray-500 mt-1">{activity.details}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">{activity.timestamp}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button
                onClick={() => navigate('/dashboard/users')}
                className="p-4 bg-white rounded-xl shadow hover:shadow-lg transition-all text-center group"
              >
                <UserGroupIcon className="w-8 h-8 text-[#008B8B] mx-auto mb-2 group-hover:scale-110 transition-transform" />
                <span className="text-sm font-medium">Manage Users</span>
              </button>
              <button
                onClick={() => navigate('/dashboard/garages')}
                className="p-4 bg-white rounded-xl shadow hover:shadow-lg transition-all text-center group"
              >
                <BuildingStorefrontIcon className="w-8 h-8 text-[#FF8C00] mx-auto mb-2 group-hover:scale-110 transition-transform" />
                <span className="text-sm font-medium">Manage Garages</span>
              </button>
              <button
                onClick={() => navigate('/dashboard/admin-privileges')}
                className="p-4 bg-white rounded-xl shadow hover:shadow-lg transition-all text-center group"
              >
                <ShieldCheckIcon className="w-8 h-8 text-[#10B981] mx-auto mb-2 group-hover:scale-110 transition-transform" />
                <span className="text-sm font-medium">Permissions</span>
              </button>
              <button
                onClick={() => navigate('/dashboard/settings')}
                className="p-4 bg-white rounded-xl shadow hover:shadow-lg transition-all text-center group"
              >
                <CogIcon className="w-8 h-8 text-[#8B5CF6] mx-auto mb-2 group-hover:scale-110 transition-transform" />
                <span className="text-sm font-medium">Settings</span>
              </button>
            </div>
          </>
        )}

        {selectedView === 'users' && renderUserManagementSection()}
        {selectedView === 'garages' && renderGarageManagementSection()}
        {selectedView === 'system' && renderSystemHealthSection()}
      </main>
    </div>
  );
};

export default EnhancedAdminDashboard;